import React from "react";
import { Agent } from "../../model_gen/agent_base";
import { AgentList } from "../Agent/AgentList";

interface MentionsBoxProps {
  availableAgents: Agent[];
  mentionQuery: string;
  selectedIndex: number;
  onSelect: (agent: Agent) => void;
  isLoading?: boolean;
}

export const MentionsBox: React.FC<MentionsBoxProps> = ({
  availableAgents,
  mentionQuery,
  selectedIndex,
  onSelect,
  isLoading = false
}) => {
  // Filter agents based on search query - by name or displayName
  const filteredAgents = availableAgents.filter(agent => {
    const searchTerm = mentionQuery.toLowerCase();
    // Check displayName first (for actions/capabilities), then fall back to agent_name
    const nameToSearch = ((agent as any).displayName || agent.agent_name).toLowerCase();
    return nameToSearch.includes(searchTerm);
  });

  // Count regular agents vs actions/capabilities
  const agentCount = filteredAgents.filter(a => !(a as any).isAction).length;
  const capabilityCount = filteredAgents.filter(a => (a as any).isAction).length;

  // Show loading state even if no agents yet
  if (isLoading) {
    return (
      <div className="absolute bottom-full left-0 right-0 mb-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
        <div className="p-3 text-center">
          <p className="text-xs text-muted-foreground font-medium">Loading agents...</p>
        </div>
      </div>
    );
  }

  if (filteredAgents.length === 0) {
    return null;
  }

  // Build the count text
  const countParts = [];
  if (agentCount > 0) {
    countParts.push(`${agentCount} ${agentCount === 1 ? "agent" : "agents"}`);
  }
  if (capabilityCount > 0) {
    countParts.push(`${capabilityCount} ${capabilityCount === 1 ? "capability" : "capabilities"}`);
  }
  const countText = countParts.join(", ") + " available";

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
      <div className="p-2 border-b border-border bg-muted/30">
        <p className="text-xs text-muted-foreground font-medium">
          {countText}
        </p>
      </div>
      <div className="max-h-52 overflow-y-auto">
        <AgentList
          agents={filteredAgents}
          searchQuery={mentionQuery}
          selectedIndex={selectedIndex}
          onSelect={onSelect}
        />
      </div>
    </div>
  );
};
